import {useCallback, useEffect, useRef, useState} from 'react';
import {agentApi} from '../api';

const HEARTBEAT_MS = 30000;
const RESUME_KEY = 'agent_resume_online';

/**
 * The agent's live-tour presence. While online the dashboard beats every 30 s (the server drops an agent after 90 s
 * without one, so a crashed tab doesn't leave them "online"); closing the tab goes offline straight away.
 */
export function usePresence(agentId) {
  const [presence, setPresence] = useState({online: false, busy: false, loading: true, error: ''});
  const onlineRef = useRef(false);
  onlineRef.current = presence.online;

  useEffect(() => {
    if (!agentId) return;
    // Closing the page goes offline; a reload of the same tab comes back online (sessionStorage outlives a reload,
    // not the tab), whichever of the old page's "offline" and this read reaches the server first.
    let resume = false;
    try { resume = sessionStorage.getItem(RESUME_KEY) === '1'; sessionStorage.removeItem(RESUME_KEY); } catch { /* blocked */ }
    const load = resume ? agentApi.setPresence(true, false) : agentApi.presence();
    load
        .then((p) => setPresence({online: p.online, busy: p.busy, loading: false, error: ''}))
        .catch((e) => setPresence((s) => ({...s, loading: false, error: e.message})));
  }, [agentId]);

  const set = useCallback(async (online, busy = false) => {
    setPresence((s) => ({...s, online, busy, error: ''}));
    try {
      const p = await agentApi.setPresence(online, busy);
      setPresence({online: p.online, busy: p.busy, loading: false, error: ''});
    } catch (e) {
      setPresence((s) => ({...s, online: !online, error: `Couldn't change your status: ${e.message}`}));
    }
  }, []);

  useEffect(() => {
    if (!agentId || !presence.online) return undefined;
    const beat = () => agentApi.heartbeat()
        .then((p) => setPresence((s) => ({...s, online: p.online, busy: p.busy})))
        .catch(() => {});
    const t = setInterval(beat, HEARTBEAT_MS);
    return () => clearInterval(t);
  }, [agentId, presence.online]);

  useEffect(() => {
    const offline = () => {
      if (!onlineRef.current) return;
      try { sessionStorage.setItem(RESUME_KEY, '1'); } catch { /* blocked */ }
      agentApi.setPresence(false, false, {keepalive: true}).catch(() => {});
    };
    window.addEventListener('pagehide', offline);
    return () => window.removeEventListener('pagehide', offline);
  }, []);

  return {...presence, setOnline: (online) => set(online, false), markBusy: (busy) => setPresence((s) => ({...s, busy}))};
}
